import moment from 'moment'
import that from '../../translationHelper'
import whenDefined from '../whenDefined'
import removeUndefined from '../removeUndefined'

// TODO: break this up better, this is the helper class for statistics: calculation, modification, display etc.

// all of the converter functions *from* any unit *to* any other (possible) unit
const converters = {
	// distance
	cm: {
		in: value => 0.393701 * value,
		ft_in: value => converters.in.ft_in(converters.cm.in(value))
	},
	in: {
		cm: value => 2.54 * value,
		ft_in: value => ({ ft: Math.floor(value / 12), in: value % 12 })
	},
	ft_in: {
		in: value => value.ft * 12 + value.in,
		cm: value => converters.in.cm(converters.ft_in.in(value))
	},
	// mass
	kg: {
		lb: value => 2.20462 * value
	},
	lb: {
		kg: value => 0.453592 * value
	}
}

const toNumber = value => +value

const scale = places => (value) => {
	const multiplier = 10 ** places
	// close enough...
	return Math.round(value * multiplier) / multiplier
}

// all of the units which can be 'converted to' from unitFrom
const conversionsFrom = unitFrom => Object.keys(converters[unitFrom] || {})
// perform the conversion of value from unitFrom to unitTo
export const convertFromTo = (unitFrom, unitTo, value) => (unitFrom !== unitTo ? converters[unitFrom][unitTo](value) : value)

// get all of the possible conversions of value from unitFrom
const convertFrom = (unitFrom, value) => conversionsFrom(unitFrom).reduce((conversions, unitTo) => ({
	...conversions,
	[unitTo]: convertFromTo(unitFrom, unitTo, value)
}), {})

const calculate = (unit, value) => whenDefined(unit, value)(() => ({
	unit,
	value: {
		[unit]: toNumber(value),
		...convertFrom(unit, value)
	},
	isPublic: true
}))

const set = (unit, value) => whenDefined(value)(() => ({ unit, value: { [unit]: value }, isPublic: true }))

const calculateStatistics = statisticsInput => removeUndefined({
	height: calculate(statisticsInput.heightUnit, statisticsInput.height),
	weight: calculate(statisticsInput.weightUnit, statisticsInput.weight),
	dateOfBirth: whenDefined(statisticsInput.dateOfBirth)(() => ({ unit: 'date', value: { date: moment.utc(statisticsInput.dateOfBirth).toISOString() }, isPublic: true })),
	chest: calculate(statisticsInput.chestUnit, statisticsInput.chest),
	waist: calculate(statisticsInput.waistUnit, statisticsInput.waist),
	stomachShape: set('stomachShape', statisticsInput.stomachShape),
	chestShape: set('chestShape', statisticsInput.chestShape),
	fitPreference: set('fitPreference', statisticsInput.fitPreference)
})

// Display functions

const statisticName = {
	height: that.context.translate('Height'),
	weight: that.context.translate('Weight'),
	dateOfBirth: that.context.translate('DOB'),
	chest: that.context.translate('Chest'),
	waist: that.context.translate('Waist'),
	chestShape: that.context.translate('Chest Shape'),
	fitPreference: that.context.translate('Fit Preferences'),
	stomachShape: that.context.translate('Stomach Shape')
}

export const statisticNameFor = key => statisticName[key] || key

const statisticUnit = {
	cm: that.context.translate('cm'),
	in: that.context.translate('in'),
	ft_in: that.context.translate('ft+in'),
	kg: that.context.translate('kg'),
	lb: that.context.translate('lb'),
	date: that.context.translate('date')
}

export const statisticUnitFor = key => statisticUnit[key] || key

const defaultDisplayValue = value => `${value}`
const defaultDiffValue = (valueA, valueB) => valueA - valueB
const defaultRelativeValue = value => value

const defaultValue = {
	display: defaultDisplayValue,
	diff: defaultDiffValue,
	diffDisplay: value => defaultValue.display(value),
	relative: defaultRelativeValue
}

const statisticValue = {
	cm: {
		...defaultValue,
		display: value => that.context.translate('{value} cm', { value: scale(1)(value) }),
		diffDisplay: value => statisticValue.cm.display(value)
	},
	in: {
		...defaultValue,
		display: value => that.context.translate('{value} in', { value: scale(2)(value) }),
		diffDisplay: value => statisticValue.in.display(value)
	},
	ft_in: {
		display: value => that.context.translate('{ft} ft {in} in', { ft: value.ft, in: scale(2)(value.in) }),
		diff: (valueA, valueB) => converters.in.ft_in(converters.ft_in.in(valueA) - converters.ft_in.in(valueB)),
		diffDisplay: value => statisticValue.ft_in.display(value),
		relative: value => converters.ft_in.in(value)
	},
	kg: {
		...defaultValue,
		display: value => that.context.translate('{value} kg', { value: scale(1)(value) }),
		diffDisplay: value => statisticValue.kg.display(value)
	},
	lb: {
		...defaultValue,
		display: value => that.context.translate('{value} lb', { value: scale(1)(value) }),
		diffDisplay: value => statisticValue.lb.display(value)
	},
	date: {
		display: value => value.format('DD/MM/YYYY'),
		diff: (valueA, valueB) => valueB.diff(valueA, 'years'),
		diffDisplay: value => that.context.translate('{years|1 year|# years}', { years: value }),
		relative: value => moment().diff(value, 'years')
	},
	chestShape: {
		...defaultValue,
		diff: (valueA, valueB) => statisticValue.chestShape.relative(valueA) - statisticValue.chestShape.relative(valueB),
		relative: (value) => {
			switch (value) {
			case 'narrow':
				return 1
			case 'broad':
				return 9
			case 'average':
			default:
				return 5
			}
		}
	},
	stomachShape: {
		...defaultValue,
		diff: (valueA, valueB) => statisticValue.stomachShape.relative(valueA) - statisticValue.stomachShape.relative(valueB),
		relative: (value) => {
			switch (value) {
			case 'flat':
				return 1
			case 'round':
				return 9
			case 'average':
			default:
				return 5
			}
		}
	},
	fitPreference: {
		...defaultValue,
		diff: (valueA, valueB) => statisticValue.fitPreference.relative(valueA) - statisticValue.fitPreference.relative(valueB),
		relative: (value) => {
			switch (value) {
			case 'tight':
				return 1
			case 'loose':
				return 9
			case 'average':
			default:
				return 5
			}
		}
	}
}

export const statisticValueFor = unit => statisticValue[unit] || defaultValue

// transform the values stored in the firestore back into something we can use
const parsers = {
	date: value => moment(value, moment.ISO_8601)
}

const parserNone = value => value

export const parserFor = unit => parsers[unit] || parserNone

const defaultComparison = {
	bigger: diff => that.context.translate('{diff} more', { diff }),
	smaller: diff => that.context.translate('{diff} less', { diff }),
	equal: that.context.translate('Equal')
}

const statisticComparison = {
	height: {
		...defaultComparison,
		bigger: diff => that.context.translate('{diff} taller', { diff }),
		smaller: diff => that.context.translate('{diff} shorter', { diff })
	},
	weight: {
		...defaultComparison,
		bigger: diff => that.context.translate('{diff} heavier', { diff }),
		smaller: diff => that.context.translate('{diff} lighter', { diff })
	},
	dateOfBirth: {
		// date diffing returns age in years
		...defaultComparison,
		bigger: diff => that.context.translate('{diff} older', { diff }),
		smaller: diff => that.context.translate('{diff} younger', { diff })
	},
	chest: {
		...defaultComparison,
		bigger: diff => that.context.translate('{diff} broader', { diff }),
		smaller: diff => that.context.translate('{diff} narrower', { diff })
	},
	chestShape: {
		bigger: () => that.context.translate('Broader'),
		smaller: () => that.context.translate('Narrower'),
		equal: that.context.translate('Same')
	},
	stomachShape: {
		bigger: () => that.context.translate('Rounder'),
		smaller: () => that.context.translate('Flatter'),
		equal: that.context.translate('Same')
	},
	fitPreference: {
		bigger: () => that.context.translate('Looser'),
		smaller: () => that.context.translate('Tighter'),
		equal: that.context.translate('Same')
	}
}

export const statisticComparisonFor = key => statisticComparison[key] || defaultComparison

const genderDisplay = {
	m: that.context.translate('Male'),
	f: that.context.translate('Female'),
	u: that.context.translate('Unisex')
}

const defaultGenderDisplay = that.context.translate('Unknown')

export const genderDisplayFor = key => genderDisplay[key] || defaultGenderDisplay

export default calculateStatistics



// WEBPACK FOOTER //
// ./src/utilities/Data/User/calculateStatistics.js